import {Button, Dialog, Text, Tile, Toast} from "@wakelet/ui-kit";
import {translateKey} from "../language/translate";
import {FullPageSpinner, Spinner} from "../loading/Spinner";
import {PasswordInput} from "../form/PasswordInput";
import {inputClass} from "../form/input-class";
import {FC} from "react";
import {useForm} from "react-hook-form";
import {FieldGroup, ValidationError} from "../form/ValidationError";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {useResetPassword} from "./useResetPassword";
import {useNavigate} from "react-router";
import {getWakeletUrl} from "../../env/env";
import {FieldLabel, RequiredFieldsMessage} from "../form/FieldLabel";

type SetNewPasswordValues = {
  newPassword: string;
  confirmPassword: string;
  serverError: void;
};

const schema = yup.object({
  newPassword: yup.string().required(translateKey("account:validation.required.newPassword")).min(8, translateKey("account:validation.password")),
  confirmPassword: yup.string().required(translateKey("account:validation.required.confirmPassword")).equals([yup.ref("newPassword")], translateKey("account:validation.required.matchPasswords")),
}).required();

type SetNewPasswordProps = {
  oobCode: string;
}

export const SetNewPassword: FC<SetNewPasswordProps> = ({oobCode}) => {
  const navigate = useNavigate();
  const {resetPassword, isLoading, isSubmitting, isSuccess, clearSuccess, isError, clearError} = useResetPassword(2000);
  if (isLoading) {
    return <FullPageSpinner/>
  }
  if (!resetPassword) {
    window.location.assign(getWakeletUrl());
    return null;
  }
  const {handleSubmit, register, formState: {errors}, setError, setValue} = useForm<SetNewPasswordValues>({
    resolver: yupResolver(
      schema),
  });

  const doSubmit = async (values: SetNewPasswordValues) => {
    if (!resetPassword) {
      return;
    }
    return resetPassword(oobCode, values.newPassword).then(() => {
      setValue("newPassword", "");
      setValue("confirmPassword", "");
    }).catch(err => {
      const {i18nKey} = err;
      setError("serverError", {message: i18nKey.key});
    });
  };

  const returnToLogin = () => {
    clearSuccess();
    navigate("/login")
  }

  const postSaveActions = <div>
    <Dialog title={translateKey("account:change.password.success.title")} data-testid="success-dialog" open={isSuccess}
            onClose={returnToLogin}>
      <Text>{translateKey("account:change.password.success.message")}</Text>
      <div className="flex justify-end gap-3 mt-3 mr-4">
        <Button data-testid="success-dialog-btn-redirect"
                variant="primary"
                onClick={returnToLogin}
        >
          <Button.Label text={translateKey("account:return.toLogin")}/>
        </Button>
      </div>
    </Dialog>
    <Toast type="error" description={translateKey("account:change.password.error.message")} data-testid="error-toast"
           open={isError}
           onOpenChange={clearError}/>
  </div>

  return <div className="w-full" data-testid="password-reset-container">
    <Tile>
      <Text.Heading level={3}>{translateKey("account:resetPassword.title")}</Text.Heading>
      <form onSubmit={handleSubmit(doSubmit)}
            aria-describedby={errors.serverError?.message && "error-resetting-password"}>
        <ValidationError textCenter={true} message={errors.serverError?.message} id="error-resetting-password"/>
        <FieldGroup>
          <FieldLabel htmlFor="new-password">{translateKey("account:password.label.new")}</FieldLabel>
          <PasswordInput
            id="new-password"
            type="password"
            {...register("newPassword")}
            className={inputClass()}
            disabled={isSubmitting}
            data-testid="new-password"
            placeholder={translateKey("account:password.label.new")}
            autoComplete="new-password"
            aria-describedby={errors.newPassword && "new-password-error"}
            aria-invalid={errors.newPassword && "true"}
          />
          <ValidationError message={errors.newPassword?.message} id="new-password-error"/>
        </FieldGroup>
        <FieldGroup>
          <FieldLabel htmlFor="confirm-password">{translateKey("account:password.label.confirm")}</FieldLabel>
          <PasswordInput
            id="confirm-password"
            type="password"
            {...register("confirmPassword")}
            className={inputClass()}
            disabled={isSubmitting}
            data-testid="confirm-password"
            placeholder={translateKey("account:password.label.confirm")}
            autoComplete="confirm-password"
            aria-describedby={errors.confirmPassword && "confirm-password-error"}
            aria-invalid={errors.confirmPassword && "true"}
          />
          <ValidationError message={errors.confirmPassword?.message} id="confirm-password-error"/>
        </FieldGroup>
        <RequiredFieldsMessage/>
        <Button type="submit" disabled={isSubmitting} data-testid="save-btn"
                className="w-32 flex justify-center items-center">
          {!isSubmitting && <Button.Label text={translateKey("common:buttons.save")}/>}
          {isSubmitting && <Spinner/>}
        </Button>
        {postSaveActions}
      </form>
    </Tile>
  </div>
};
